import React from 'react';
import { useTranslation } from 'localization';
import { UnifiedFilters, FilterExpression } from 'types/filters';
import type { PropertyOption } from '../PropertyFilter/PropertyFilterMolecule';
import { prettifyKey } from '../../../utils/filterPropertyBuilder';
import './ApplicabilitySummaryMolecule.scss';

export interface ApplicabilitySummaryMoleculeProps {
  applicability: UnifiedFilters | null;
  /**
   * The schema-driven property catalog (SC1) — labels resolve by `property`
   * (the `key` a stored filter carries), then by `aspectIdentity` for a
   * composed reference. `PROPERTY_LABEL_MAP` (a hardcoded name→label map that
   * drifted from the real catalog) is gone; omitting this prop falls back to
   * `prettifyKey`, never a second hardcoded map.
   */
  properties?: PropertyOption[];
  className?: string;
}

// Map known graph label values to friendly display names
const LABEL_VALUE_MAP: Record<string, string> = {
  metasphere: 'Metasphere (Organization Root)',
  center: 'Center',
};

function formatOperator(operator: string): string {
  return operator.replaceAll('_', ' ');
}

function formatPropertyName(filter: FilterExpression, properties: PropertyOption[]): string {
  const byKey = properties.find((p) => p.key === filter.property);
  if (byKey) return byKey.label;
  if (filter.aspect) {
    const byAspectIdentity = properties.find((p) => p.aspectIdentity === `${filter.aspect}.${filter.property}`);
    if (byAspectIdentity) return byAspectIdentity.label;
  }
  return prettifyKey(filter.property);
}

function formatPropertyValue(property: string, value: string): string {
  if (property === 'label' || property === 'type') {
    return LABEL_VALUE_MAP[value] ?? value
      .replace(/^center_/, '')
      .replaceAll('_', ' ')
      .replaceAll(/\b\w/g, (c) => c.toUpperCase());
  }
  return value;
}

function formatFilterExpression(
  filter: FilterExpression,
  t: (key: string, opts?: Record<string, unknown>) => string,
  properties: PropertyOption[]
): string {
  const rawValue =
    filter.value === undefined
      ? filter.values?.join(', ') ?? ''
      : String(filter.value);

  const friendlyProperty = formatPropertyName(filter, properties);
  const friendlyValue = filter.values
    ? filter.values.map((v) => formatPropertyValue(filter.property, String(v))).join(', ')
    : formatPropertyValue(filter.property, rawValue);

  return t('marketplace.approachDetail.applicability.filterDescription', {
    property: friendlyProperty,
    operator: formatOperator(filter.operator),
    value: friendlyValue,
  });
}

function hasOnlyPropertyFilters(filters: UnifiedFilters): boolean {
  const hasPropertyFilters =
    Array.isArray(filters.propertyFilters) &&
    filters.propertyFilters.length > 0;
  const hasFilterGroups =
    Array.isArray(filters.filterGroups) && filters.filterGroups.length > 0;
  const hasPerspectiveFilters =
    Array.isArray(filters.perspectiveFilters) &&
    filters.perspectiveFilters.length > 0;

  return hasPropertyFilters && !hasFilterGroups && !hasPerspectiveFilters;
}

const ApplicabilitySummaryMolecule: React.FC<
  ApplicabilitySummaryMoleculeProps
> = ({ applicability, properties = [], className = '' }) => {
  const { t } = useTranslation();

  const classes = ['marketplace-applicability-summary-molecule', className]
    .filter(Boolean)
    .join(' ');

  const renderContent = () => {
    if (!applicability) {
      return (
        <div className="marketplace-applicability-summary-molecule__any-sphere">
          <i className="pi pi-check-circle" />
          <div>
            <div className="marketplace-applicability-summary-molecule__any-sphere-label">
              {t('marketplace.approachDetail.applicability.anySphere')}
            </div>
            <div className="marketplace-applicability-summary-molecule__any-sphere-description">
              {t(
                'marketplace.approachDetail.applicability.anySphereDescription'
              )}
            </div>
          </div>
        </div>
      );
    }

    if (hasOnlyPropertyFilters(applicability)) {
      const filters = applicability.propertyFilters ?? [];

      if (filters.length === 1) {
        return (
          <div className="marketplace-applicability-summary-molecule__single-filter">
            <i className="pi pi-filter" />
            <span>{formatFilterExpression(filters[0], t, properties)}</span>
          </div>
        );
      }

      return (
        <ul className="marketplace-applicability-summary-molecule__filter-list">
          {filters.map((filter, index) => (
            <li key={index}>
              <i className="pi pi-filter" />
              <span>{formatFilterExpression(filter, t, properties)}</span>
            </li>
          ))}
        </ul>
      );
    }

    return (
      <div className="marketplace-applicability-summary-molecule__complex-fallback">
        <i className="pi pi-info-circle" />
        <span>
          {t('marketplace.approachDetail.applicability.complexFallback')}
        </span>
      </div>
    );
  };

  return (
    <div className={classes}>
      <div className="marketplace-applicability-summary-molecule__header">
        {t('marketplace.approachDetail.applicability.title')}
      </div>
      {renderContent()}
    </div>
  );
};

export default ApplicabilitySummaryMolecule;
